import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const WATERMARK_ASSET_URL = 'assets/theme/images/logo/watermark.svg';

/** One watermark bitmap shared by every canvas (was decoded again for each card). */
let watermarkPromise: Promise<HTMLImageElement | null> | null = null;

function loadWatermark(): Promise<HTMLImageElement | null> {
  if (!watermarkPromise) {
    watermarkPromise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = WATERMARK_ASSET_URL;
    });
  }
  return watermarkPromise;
}

function runWhenIdle(fn: () => void): void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 50);
  }
}

@Component({
  selector: 'app-svg-canvas',
  standalone: true,
  imports: [],
  templateUrl: './svg-canvas.component.html',
  styleUrls: ['./svg-canvas.component.css'],
})
export class SvgCanvasComponent implements OnInit, OnChanges, OnDestroy {
  private readonly watermarkOverlayAlpha = 1;

  @Input() width: number = 80;
  @Input() height: number = 80;
  @Input() filePath: string = '';
  @Input() tilte?: string;
  @Input() iconName?: string;
  @Input() isRefresh: boolean = false;
  /**
   * When true the image is fetched and drawn only once the canvas is near the viewport.
   * Use it for long grids; short lists and carousels keep the default (draw immediately).
   */
  @Input() lazy: boolean = false;
  /** Emits once the current image has been drawn on the canvas. */
  @Output() drawn = new EventEmitter<void>();
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  hiddenImageSrc: string = '';

  private readonly isBrowser: boolean;
  private intersection?: IntersectionObserver;
  private inView = false;
  private destroyed = false;
  /** Guards against an older, slower image overwriting a newer one. */
  private drawRequest = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.preventCanvasDataExtraction();

    if (this.lazy && typeof IntersectionObserver !== 'undefined') {
      this.ngZone.runOutsideAngular(() => {
        this.intersection = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.inView = true;
              this.intersection?.disconnect();
              this.refreshCanvas();
            }
          },
          { rootMargin: '300px 0px' }
        );
        this.intersection.observe(this.canvasRef.nativeElement);
      });
      return;
    }

    this.inView = true;
    this.refreshCanvas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isBrowser) {
      return;
    }
    const fp = changes['filePath'];
    // Reload when SAS arrives or the asset URL changes after first paint.
    if (fp && this.filePath && !fp.firstChange && fp.previousValue !== fp.currentValue) {
      this.refreshCanvas();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.intersection?.disconnect();
  }

  /** Fetches the image once, draws it, then builds the SEO composite from the same bitmap. */
  private loadSvgToCanvas(svgPath: string): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx || !svgPath) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    canvas.width = this.width * dpr;
    canvas.height = this.height * dpr;
    canvas.style.width = `${this.width}px`;
    canvas.style.height = `${this.height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const request = ++this.drawRequest;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      if (this.destroyed || request !== this.drawRequest) {
        return;
      }
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.drawImage(img, 0, 0, this.width, this.height);
      this.ngZone.run(() => this.drawn.emit());
      runWhenIdle(() => this.buildSeoCompositeImage(img, svgPath, request));
    };
    img.onerror = () => {
      console.error('Failed to load the SVG/image preview.');
    };
    img.src = svgPath;
  }

  drawWatermarkCover(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    wm: HTMLImageElement,
    alpha: number
  ): void {
    const iw = wm.naturalWidth || wm.width;
    const ih = wm.naturalHeight || wm.height;
    if (iw <= 0 || ih <= 0) {
      return;
    }

    const scale = Math.max(canvasW / iw, canvasH / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (canvasW - dw) / 2;
    const dy = (canvasH - dh) / 2;

    const prevAlpha = ctx.globalAlpha;
    ctx.globalAlpha = alpha;
    ctx.drawImage(wm, dx, dy, dw, dh);
    ctx.globalAlpha = prevAlpha;
  }

  /**
   * Hidden watermarked <img> for crawlers. Built from the bitmap that is already loaded,
   * when the browser is idle, at 1x (it is never shown, so no device-pixel scaling).
   */
  private async buildSeoCompositeImage(
    mainImage: HTMLImageElement,
    svgPath: string,
    request: number
  ): Promise<void> {
    if (this.destroyed || request !== this.drawRequest) {
      return;
    }

    const w = this.width;
    const h = this.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(mainImage, 0, 0, w, h);

    const watermark = await loadWatermark();
    if (this.destroyed || request !== this.drawRequest) {
      return;
    }
    if (watermark) {
      this.drawWatermarkCover(ctx, w, h, watermark, this.watermarkOverlayAlpha);
    }

    let src: string;
    try {
      src = canvas.toDataURL('image/png');
    } catch {
      src = svgPath;
    }
    this.ngZone.run(() => {
      this.hiddenImageSrc = src;
      this.cdr.detectChanges();
    });
  }

  preventCanvasDataExtraction(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
    canvas.toDataURL = function () {
      console.error('Access to toDataURL is disabled');
      return '';
    };
    canvas.toBlob = function () {
      console.error('toBlob method is disabled');
    };
  }

  clearCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  refreshCanvas(): void {
    if (!this.isBrowser || !this.filePath || !this.inView) {
      return;
    }
    this.clearCanvas();
    this.ngZone.runOutsideAngular(() => this.loadSvgToCanvas(this.filePath));
  }

  gettitle() {
    if (this.iconName) {
      return this.iconName!.length > 10
        ? this.iconName?.slice(0, 10) + '...'
        : this.iconName;
    }
    return '';
  }
}
